import { GarmentConversion } from './GarmentConversion';
import type { SizeGuidePatch } from '../types';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Combobox,
  Divider,
  InlineStack,
  Listbox,
  Tag,
  Text,
  TextField,
} from '@shopify/polaris';
import { useLanguage } from '../providers/LanguageProvider';
import { useShopify } from '../providers/AppBridgeProvider';
import {
  MAX_TARGETING_LIST_LENGTH,
  type SizeCoverageReport,
  type SizeGuideCategories,
  type SizeGuideEntryView,
  type StructuredSizeGuide,
} from '../types';
import type { SizeGuideErrorState } from '../hooks/useSettings';
import type { Translations } from '../i18n/types';

/** Mirrors MAX_SIZE_GUIDES in web/backend/src/models/merchantSettings.ts. */
export const MAX_SIZE_GUIDES = 20;

/** Rows shown before the table is folded. A multi-garment guide can carry
 * forty of them, which would push the rest of the card off screen. */
const PREVIEW_ROWS = 12;

/** Canonical order the engine's own measurements follow, head to foot, so
 * columns do not shuffle between two uploads of the same guide. */
const MEASUREMENT_ORDER = [
  'height_cm',
  'neck_cm',
  'shoulder_width_cm',
  'chest_cm',
  'underbust_cm',
  'waist_cm',
  'hip_cm',
  'thigh_cm',
  'inseam_cm',
  'sleeve_length_cm',
  'foot_length_cm',
];

/** Every measurement key any row carries, in a stable order, so a guide that
 * only lists chest and waist does not render nine empty columns. */
function usedMeasurementKeys(guide: StructuredSizeGuide): string[] {
  const seen = new Set<string>();
  for (const row of guide.rows) {
    for (const [key, value] of Object.entries(row.measurements ?? {})) {
      if (value !== null && value !== undefined) seen.add(key);
    }
  }
  const known = MEASUREMENT_ORDER.filter((key) => seen.has(key));
  // Anything the engine adds later still gets a column, under its raw key:
  // an unlabelled measurement is worth more to the merchant than a hidden one.
  const unknown = [...seen].filter((key) => !MEASUREMENT_ORDER.includes(key)).sort();
  return [...known, ...unknown];
}

/**
 * Turns a size guide mutation's error code into copy a merchant can act on.
 * Shared between this card (patch/delete failures land here, correlated to
 * the entry that caused them) and SizeGuidesSection (create failures, which
 * have no entry yet to attach to) so the same code always reads the same way
 * regardless of which request produced it.
 */
export function sizeGuideErrorMessage(s: Translations['settings'], err: SizeGuideErrorState): string {
  let message: string;
  switch (err.code) {
    case 'unreadable_guide':
    case 'unparseable_guide':
    case 'invalid_parse_output':
      message = s.sizeGuideErrorUnreadable; break;
    case 'empty_file':
    case 'file_signature_mismatch':
    case 'pdf_rasterization_failed':
      message = s.sizeGuideErrorInvalidFile; break;
    case 'image_unreadable':
      message = s.sizeGuideErrorImageUnreadable; break;
    case 'no_text_detected':
      message = s.sizeGuideErrorNoText; break;
    case 'no_table_detected':
      message = s.sizeGuideErrorNoTable; break;
    case 'cells_not_extractable':
      message = s.sizeGuideErrorCells; break;
    case 'no_valid_size_rows':
      message = s.sizeGuideErrorNoRows; break;
    case 'parser_unavailable':
      message = s.sizeGuideErrorParserDown; break;
    case 'file_too_large':
      message = s.sizeGuideErrorTooLarge; break;
    case 'unsupported_file_type':
    case 'invalid_upload':
    case 'missing_file':
      message = s.sizeGuideErrorUnsupportedType; break;
    case 'too_many_guides':
      return s.sizeGuideErrorTooMany(MAX_SIZE_GUIDES);
    case 'invalid_name':
      return s.sizeGuideErrorInvalidName;
    case 'invalid_products':
    case 'catalog_unavailable':
      return s.sizeGuideProductsUnavailable;
    case 'category_conflict':
      return s.sizeGuideErrorConflict(err.conflictName ?? '');
    case 'duplicate_fallback':
      return s.sizeGuideErrorSecondFallback(err.conflictName ?? '');
    case 'revision_conflict':
    case 'settings_changed':
      return s.sizeGuideErrorSettingsChanged;
    default:
      // guide_not_found, network_error, http_xxx, and anything the backend
      // adds later: still a failure the merchant needs to see, but the raw
      // code is engine vocabulary and means nothing here.
      return s.sizeGuideErrorGeneric;
  }
  const parseCodes = new Set([
    'unreadable_guide', 'unparseable_guide', 'invalid_parse_output', 'empty_file',
    'file_signature_mismatch', 'pdf_rasterization_failed', 'image_unreadable',
    'no_text_detected', 'no_table_detected', 'cells_not_extractable', 'no_valid_size_rows',
  ]);
  if (parseCodes.has(err.code)) message += ` ${s.sizeGuideAlternative}`;
  if (err.correlationId) message += ` ${s.sizeGuideErrorReference(err.correlationId)}`;
  return message;
}

export interface SizeGuideCardProps {
  entry: SizeGuideEntryView;
  /** This guide's row from the last coverage check, or undefined when the
   * check has never run against it (e.g. a guide created since). */
  coverage?: SizeCoverageReport['guides'][number];
  /** Set when the report as a whole carries an error — shop-wide, not
   * specific to this guide. Only shown here when `coverage` above is ALSO
   * absent or has `sampled: 0`: a partial report keeps real numbers for
   * every guide it actually reached before the failure, and those numbers
   * win over this flag; a guide with no real sample of its own does not. */
  coverageError?: string | null;
  /** Shopify product types already seen in the shop, for the combobox.
   * Empty is normal and must not block free text. */
  productTypeSuggestions: string[];
  onPatch: (id: string, patch: SizeGuidePatch) => void;
  onRefresh?: (id: string) => Promise<void>;
  onDelete: (id: string) => void;
  busy: boolean;
  errorCode: SizeGuideErrorState | null;
  disabled?: boolean;
}

export function SizeGuideCard({
  entry,
  coverage,
  coverageError = null,
  productTypeSuggestions,
  onPatch,
  onRefresh,
  onDelete,
  busy,
  errorCode,
  disabled = false,
}: SizeGuideCardProps) {
  const { t, language } = useLanguage();
  const s = t.settings;
  const { shopify } = useShopify();
  const authFetch = useAuthenticatedFetch();
  const [products, setProducts] = useState<Record<string, { title: string; image: string | null; status: string; published: boolean }>>({});
  const productKey = (entry.categories.productIds ?? []).join(',');
  useEffect(() => {
    if (!productKey) return;
    const controller = new AbortController();
    void authFetch(`/api/settings/guide-products?ids=${productKey}`, { signal: controller.signal })
      .then(async (response) => { if (!response.ok) throw new Error(); return response.json(); })
      .then((body: { products: Array<{ id: string; title: string; image: string | null; status: string; published: boolean }> }) => {
        if (!controller.signal.aborted) setProducts(Object.fromEntries(body.products.map((product) => [product.id, product])));
      }).catch(() => { if (!controller.signal.aborted) setPickerError(true); });
    return () => controller.abort();
  }, [authFetch, productKey]);
  const resourcePicker = shopify?.resourcePicker;
  const pickerAvailable = typeof resourcePicker === 'function';

  const guide = entry.structuredSizeGuide;
  const categories = entry.categories;
  const controlsDisabled = disabled || busy;

  const [showAllRows, setShowAllRows] = useState(false);
  const [nameDraft, setNameDraft] = useState(entry.name);
  const [typeInput, setTypeInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [pickerError, setPickerError] = useState(false);

  // Re-sync the draft when the SERVER's name changes (our own save landing,
  // or a fresh read after a concurrent edit). Keyed on the string value, not
  // the entry's identity, so a re-render triggered by editing a DIFFERENT
  // guide never clobbers an in-progress, unsaved rename here.
  useEffect(() => {
    setNameDraft(entry.name);
  }, [entry.name]);

  const trimmedName = nameDraft.trim();
  const canSaveName = trimmedName.length > 0 && trimmedName !== entry.name && !controlsDisabled;

  const patchCategories = useCallback(
    (next: SizeGuideCategories) => {
      onPatch(entry.id, { categories: next });
    },
    [onPatch, entry.id]
  );

  const pickCollections = useCallback(async () => {
    if (!resourcePicker) return;
    const current = categories.collections;
    const remaining = MAX_TARGETING_LIST_LENGTH - current.length;
    if (remaining <= 0) return;
    setPickerError(false);
    try {
      // Only handles are stored, so there is no GID to preselect the picker
      // with: it opens empty and its answer is ADDED, matching
      // TargetingSection's pick() — replacing would silently drop whatever
      // was chosen on an earlier visit.
      const selection = await resourcePicker({
        type: 'collection',
        action: 'add',
        multiple: remaining,
      });
      if (!selection) return;

      const handles = [...current];
      for (const item of selection) {
        const handle = item.handle?.trim();
        if (!handle) {
          setPickerError(true);
          return;
        }
        if (!handles.includes(handle)) handles.push(handle);
      }
      patchCategories({ ...categories, collections: handles.slice(0, MAX_TARGETING_LIST_LENGTH) });
    } catch (err) {
      console.error('[SizeGuideCard] resource picker failed:', err);
      setPickerError(true);
    }
  }, [resourcePicker, categories, patchCategories]);

  const pickProducts = async () => {
    if (!resourcePicker) return;
    setPickerError(false);
    try {
      const selected = await resourcePicker({ type: 'product', action: 'select', multiple: MAX_TARGETING_LIST_LENGTH,
        selectionIds: (categories.productIds ?? []).map((id) => ({ id: `gid://shopify/Product/${id}` })),
        filter: { variants: false, draft: false, archived: false, hidden: false },
      });
      if (!selected) return;
      const ids = selected.map((product) => product.id.match(/^gid:\/\/shopify\/Product\/([1-9][0-9]*)$/)?.[1]);
      if (ids.some((id) => !id)) { setPickerError(true); return; }
      patchCategories({ ...categories, productIds: [...new Set(ids as string[])].slice(0, MAX_TARGETING_LIST_LENGTH) });
    } catch { setPickerError(true); }
  };

  const addProductType = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed || categories.productTypes.includes(trimmed)) return;
    patchCategories({ ...categories, productTypes: [...categories.productTypes, trimmed] });
    setTypeInput('');
  };

  const removeProductType = (value: string) => {
    patchCategories({ ...categories, productTypes: categories.productTypes.filter((v) => v !== value) });
  };

  const addTag = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed || categories.tags.includes(trimmed)) return;
    patchCategories({ ...categories, tags: [...categories.tags, trimmed] });
    setTagInput('');
  };

  const removeTag = (value: string) => {
    patchCategories({ ...categories, tags: categories.tags.filter((v) => v !== value) });
  };

  const removeCollection = (value: string) => {
    patchCategories({ ...categories, collections: categories.collections.filter((v) => v !== value) });
  };

  // Enter always commits whatever is typed, matching or not. Both handlers
  // stop the native event from reaching Polaris's own document-level Enter
  // listener (Listbox auto-selects a highlighted suggestion on Enter while
  // the combobox is focused) so a merchant typing free text never also gets
  // a suggestion added underneath it.
  const handleTypeKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    addProductType(typeInput);
  };

  const handleTagKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    addTag(tagInput);
  };

  const typeSuggestions = productTypeSuggestions.filter(
    (type) =>
      !categories.productTypes.includes(type) &&
      type.toLowerCase().includes(typeInput.trim().toLowerCase())
  );

  const isFallback =
    (categories.productIds?.length ?? 0) === 0 &&
    categories.productTypes.length === 0 &&
    categories.collections.length === 0 &&
    categories.tags.length === 0;

  const errorMessage = errorCode ? sizeGuideErrorMessage(s, errorCode) : null;

  const basisBadge = () => {
    if (guide.measurement_basis === 'body') {
      return <Badge tone="success">{s.sizeGuideBasisBody}</Badge>;
    }
    return (
      <Badge tone="warning">
        {guide.measurement_basis === 'garment' ? s.sizeGuideBasisGarment : s.sizeGuideBasisUnknown}
      </Badge>
    );
  };

  /**
   * The unit the parser believed the chart was in. Every value in the table
   * below is centimetres whatever the answer was, so a chart misread as
   * inches shifts every recommendation exactly the way a wrong basis does,
   * and the only person who can spot it is the merchant comparing these
   * numbers to their own chart.
   */
  const sourceUnitBadge = () =>
    guide.source_unit === 'in' ? (
      <Badge tone="attention">{s.sizeGuideSourceUnitIn}</Badge>
    ) : (
      <Badge>{s.sizeGuideSourceUnitCm}</Badge>
    );

  const renderTable = () => {
    if (guide.rows.length === 0) {
      return (
        <Banner tone="warning">
          <p>{s.sizeGuideEmpty}</p>
        </Banner>
      );
    }

    const keys = usedMeasurementKeys(guide);
    const labels = s.measurements as Record<string, string | undefined>;
    const visibleRows = showAllRows ? guide.rows : guide.rows.slice(0, PREVIEW_ROWS);
    const foldable = guide.rows.length > PREVIEW_ROWS;

    return (
      <BlockStack gap="200">
        <Text as="p" variant="bodySm" tone="subdued">
          {s.sizeGuideRowCount(guide.rows.length)}
        </Text>
        {/* A wide guide scrolls inside its own box rather than stretching the
            settings page: eleven measurements is a legitimate chart.

            ph-no-capture: the table is the merchant's own size chart — their
            grading, which is commercially theirs and nothing an analytics
            question needs. The upload's OUTCOME is recorded server-side
            (merchant_size_guide_uploaded); its CONTENTS never leave here. */}
        <div style={{ overflowX: 'auto' }} className="ph-no-capture">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 12px 4px 0', whiteSpace: 'nowrap' }}>
                  <Text as="span" variant="bodySm" fontWeight="semibold">
                    {s.sizeGuideSizeColumn}
                  </Text>
                </th>
                {keys.map((key) => (
                  <th
                    key={key}
                    style={{ textAlign: 'left', padding: '4px 12px 4px 0', whiteSpace: 'nowrap' }}
                  >
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      {labels[key] ?? key}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={`${row.size_label}-${index}`}>
                  <td style={{ padding: '4px 12px 4px 0', whiteSpace: 'nowrap' }}>
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      {row.size_label}
                    </Text>
                  </td>
                  {keys.map((key) => {
                    const value = Object.entries(row.measurements).find(([dimension]) => dimension === key)?.[1];
                    return (
                      <td key={key} style={{ padding: '4px 12px 4px 0', whiteSpace: 'nowrap' }}>
                        <Text as="span" variant="bodySm">
                          {value === null || value === undefined ? '-' : String(value)}
                        </Text>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {foldable && (
          <Box>
            <Button variant="plain" onClick={() => setShowAllRows((open) => !open)}>
              {showAllRows ? s.sizeGuideShowFewer : s.sizeGuideShowAll(guide.rows.length)}
            </Button>
          </Box>
        )}
      </BlockStack>
    );
  };

  const parsedAt = guide.parsedAt ? new Date(guide.parsedAt) : null;
  const parsedAtLabel =
    parsedAt && !Number.isNaN(parsedAt.getTime())
      ? s.sizeGuideParsedAt(parsedAt.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US'))
      : null;

  const coverageRow = (() => {
    // This guide's own numbers take priority over a report-level error: the
    // coverage service preserves every guide it already sampled when a later
    // GraphQL call fails (buildCoverageReport on the backend), so `coverage`
    // being present here means it is real even when `coverageError` is also
    // set. Checking `coverageError` first would show "could not be checked"
    // for a guide the check actually finished, and would contradict
    // SizeCoverageBanner on the dashboard, which reads the same per-guide
    // data and ignores `error` for exactly this reason.
    //
    // But `coverage.sampled > 0` is required too, not just `coverage`
    // truthy: matched(0) >= sampled(0) is vacuously true, and `sampled: 0`
    // is what a guide the check has not actually examined looks like —
    // never reached at all, cut short by a shop-wide budget
    // (buildCoverageReport's own SampleResult), or the shop's fallback
    // guide (judged by the uncovered scan, never sampled on its own). None
    // of those is "fully covered", so none of them may render the OK copy;
    // they fall through to the same "not checked" treatment as a guide with
    // no report row at all.
    if (coverage && coverage.sampled > 0) {
      if (coverage.matched >= coverage.sampled) {
        return {
          text: s.sizeGuideCoverageOk(coverage.matched, coverage.sampled),
          tone: 'subdued' as const,
          examples: null as string | null,
        };
      }
      return {
        text: s.sizeGuideCoverageMismatch(coverage.matched, coverage.sampled),
        tone: 'caution' as const,
        examples: coverage.examples.length > 0 ? s.sizeGuideCoverageExamples(coverage.examples.join(', ')) : null,
      };
    }
    if (coverageError) {
      return { text: s.sizeGuideCoverageFailed, tone: 'subdued' as const, examples: null as string | null };
    }
    return { text: s.sizeGuideCoverageUnchecked, tone: 'subdued' as const, examples: null as string | null };
  })();

  return (
    <Card>
      <BlockStack gap="400">
        {errorMessage && (
          <Banner tone="critical">
            <p>{errorMessage}</p>
          </Banner>
        )}

        {pickerError && (
          <Banner tone="critical" onDismiss={() => setPickerError(false)}>
            <p>{s.targetingPickerFailed}</p>
          </Banner>
        )}

        {entry.garmentChartsEnabled && guide.measurement_basis !== 'body' && (
          <GarmentConversion entry={entry} busy={controlsDisabled} onPatch={onPatch} onRefresh={onRefresh} error={!!errorCode} />
        )}
        {guide.measurement_basis !== 'body' && !entry.garmentChartsEnabled && (
          <Banner tone="critical" title={s.sizeGuideBasisBlockedTitle}>
            <BlockStack gap="200"><p>{s.sizeGuideBasisBlockedBody}</p><p>{guide.measurement_basis === 'garment' ? s.sizeGuideBasisGarmentDetail : s.sizeGuideBasisUnknownDetail}</p></BlockStack>
          </Banner>
        )}

        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="center" wrap>
            {basisBadge()}
            {sourceUnitBadge()}
            {entry.filename && (
              // A merchant's filename is routinely their own naming: a season,
              // a client, sometimes a person. Recorded nowhere and masked here.
              <Text as="span" variant="bodySm">
                <span className="ph-no-capture">{entry.filename}</span>
              </Text>
            )}
            {parsedAtLabel && (
              <Text as="span" variant="bodySm" tone="subdued">
                {parsedAtLabel}
              </Text>
            )}
          </InlineStack>
          {entry.garmentChartsEnabled && guide.measurement_basis !== 'body' && <Text as="h3" variant="headingSm">{s.garmentSourceTable}</Text>}
          {renderTable()}
        </BlockStack>

        <Divider />

        <InlineStack gap="200" blockAlign="end" wrap={false}>
          <div style={{ flexGrow: 1, minWidth: '200px' }}>
            <TextField
              label={s.sizeGuideNameLabel}
              value={nameDraft}
              onChange={setNameDraft}
              autoComplete="off"
              disabled={controlsDisabled}
            />
          </div>
          <Button onClick={() => onPatch(entry.id, { name: trimmedName })} disabled={!canSaveName} loading={busy}>
            {s.sizeGuideNameSave}
          </Button>
        </InlineStack>

        <Divider />

        <BlockStack gap="300">
          {isFallback && <Badge tone="info">{s.sizeGuideFallbackBadge}</Badge>}

          <div className="ph-no-capture">
            <BlockStack gap="200">
              <Text as="h4" variant="bodySm" fontWeight="semibold">{s.sizeGuideProductsLabel}</Text>
              <Text as="p" variant="bodySm">{s.sizeGuideProductsHelp}</Text>
              {(categories.productIds ?? []).map((id) => <InlineStack key={id} gap="200" blockAlign="center">
                {products[id]?.image && <img src={products[id].image!} alt="" width={40} height={40} style={{ objectFit: 'cover' }} />}
                <Tag onRemove={controlsDisabled ? undefined : () => patchCategories({ ...categories, productIds: categories.productIds?.filter((value) => value !== id) })}>
                  {`${products[id]?.title ?? s.sizeGuideProductsUnavailable} · #${id} · ${products[id]?.published ? s.sizeGuideProductPublished : s.sizeGuideProductUnpublished}`}
                </Tag>
              </InlineStack>)}
              <Box><Button disabled={controlsDisabled || !pickerAvailable} onClick={() => void pickProducts()}>{s.targetingChooseProducts}</Button></Box>
            </BlockStack>
          </div>

          {/* Collections: same resource-picker pattern as TargetingSection's
              pick(), handles only, appended rather than replaced. */}
          <BlockStack gap="200">
            <Text as="h4" variant="bodySm" fontWeight="semibold">
              {s.sizeGuideCollectionsLabel}
            </Text>
            {categories.collections.length > 0 && (
              <InlineStack gap="100" wrap>
                {categories.collections.map((handle) => (
                  <Tag key={handle} onRemove={controlsDisabled ? undefined : () => removeCollection(handle)}>
                    {handle}
                  </Tag>
                ))}
              </InlineStack>
            )}
            <Box>
              <Button
                disabled={
                  controlsDisabled ||
                  !pickerAvailable ||
                  categories.collections.length >= MAX_TARGETING_LIST_LENGTH
                }
                onClick={() => {
                  void pickCollections();
                }}
              >
                {s.targetingChooseCollections}
              </Button>
            </Box>
            {!pickerAvailable && (
              <Text as="p" variant="bodySm" tone="subdued">
                {s.targetingPickerUnavailable}
              </Text>
            )}
          </BlockStack>

          {/* Product types: combobox with filtered suggestions, plus free
              text on Enter. An empty suggestion list is normal and must not
              block typing a type by hand. */}
          <BlockStack gap="200">
            <div onKeyDown={handleTypeKeyDown}>
              <Combobox
                activator={
                  <Combobox.TextField
                    label={s.sizeGuideTypesLabel}
                    value={typeInput}
                    placeholder={s.sizeGuideTypesPlaceholder}
                    autoComplete="off"
                    disabled={controlsDisabled}
                    onChange={setTypeInput}
                  />
                }
              >
                {typeSuggestions.length > 0 ? (
                  <Listbox onSelect={addProductType}>
                    {typeSuggestions.map((type) => (
                      <Listbox.Option key={type} value={type}>
                        {type}
                      </Listbox.Option>
                    ))}
                  </Listbox>
                ) : null}
              </Combobox>
            </div>
            {categories.productTypes.length > 0 && (
              <InlineStack gap="100" wrap>
                {categories.productTypes.map((type) => (
                  <Tag key={type} onRemove={controlsDisabled ? undefined : () => removeProductType(type)}>
                    {type}
                  </Tag>
                ))}
              </InlineStack>
            )}
          </BlockStack>

          {/* Shopify tags: plain text field, add on Enter. */}
          <BlockStack gap="200">
            <div onKeyDown={handleTagKeyDown}>
              <TextField
                label={s.sizeGuideTagsLabel}
                value={tagInput}
                placeholder={s.sizeGuideTagsPlaceholder}
                autoComplete="off"
                disabled={controlsDisabled}
                onChange={setTagInput}
              />
            </div>
            {categories.tags.length > 0 && (
              <InlineStack gap="100" wrap>
                {categories.tags.map((tag) => (
                  <Tag key={tag} onRemove={controlsDisabled ? undefined : () => removeTag(tag)}>
                    {tag}
                  </Tag>
                ))}
              </InlineStack>
            )}
          </BlockStack>
        </BlockStack>

        <Divider />

        {!!coverage?.invalidProductIds?.length && <Banner tone="warning"><p>{s.sizeGuideProductsUnavailable} {coverage.invalidProductIds.map((id) => `#${id}`).join(', ')}</p></Banner>}
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" tone={coverageRow.tone}>
            {coverageRow.text}
          </Text>
          {coverageRow.examples && (
            <Text as="p" variant="bodySm" tone="subdued">
              {coverageRow.examples}
            </Text>
          )}
        </BlockStack>

        <Box>
          <Button tone="critical" variant="plain" onClick={() => onDelete(entry.id)} loading={busy} disabled={disabled}>
            {s.sizeGuideRemove}
          </Button>
        </Box>
      </BlockStack>
    </Card>
  );
}

export default SizeGuideCard;
